import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Key, Loader2, Shield, Trash2, UserX } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

export default function AccountPage() {
  const { profile, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Errore',
        description: 'Le password non corrispondono',
        variant: 'destructive',
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: 'Errore',
        description: 'La password deve essere di almeno 6 caratteri',
        variant: 'destructive',
      });
      return;
    }

    setLoadingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (error) throw error;

      toast({
        title: 'Password aggiornata',
        description: 'La tua password è stata modificata con successo',
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'ELIMINA') {
      toast({
        title: 'Errore',
        description: 'Devi digitare "ELIMINA" per confermare',
        variant: 'destructive',
      });
      return;
    }

    setLoadingDelete(true);

    try {
      const { error: rpcError } = await supabase.rpc('delete_user_account', {
        user_id: user?.id
      });

      if (rpcError) throw rpcError;

      await supabase.auth.signOut();

      toast({
        title: 'Account eliminato',
        description: 'Il tuo account è stato eliminato definitivamente',
      });

      navigate('/signup');
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile eliminare l\'account. Contatta il supporto.',
        variant: 'destructive',
      });
    } finally {
      setLoadingDelete(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Impostazioni Account
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestisci sicurezza e preferenze del tuo account
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <CardTitle>Informazioni Account</CardTitle>
            </div>
            <CardDescription>
              Dettagli del tuo account utente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-3 border-b">
                <span className="text-gray-600 font-medium">Email</span>
                <span className="font-semibold">{profile?.email}</span>
              </div>
              <div className="flex justify-between py-3 border-b">
                <span className="text-gray-600 font-medium">Ruolo</span>
                <span className="font-semibold capitalize">{profile?.role}</span>
              </div>
              <div className="flex justify-between py-3 border-b">
                <span className="text-gray-600 font-medium">ID Utente</span>
                <span className="font-mono text-xs text-gray-500">{user?.id}</span>
              </div>
              <div className="flex justify-between py-3 border-b">
                <span className="text-gray-600 font-medium">Data registrazione</span>
                <span className="font-semibold">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-gray-600 font-medium">Ultimo accesso</span>
                <span className="font-semibold">
                  {user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString('it-IT', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              <CardTitle>Cambia Password</CardTitle>
            </div>
            <CardDescription>
              Aggiorna la password del tuo account per maggiore sicurezza
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nuova Password</Label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  placeholder="Inserisci la nuova password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  disabled={loadingPassword}
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500">
                  La password deve essere di almeno 6 caratteri
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Nuova Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Conferma la nuova password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  disabled={loadingPassword}
                  required
                  minLength={6}
                />
              </div>

              <Button type="submit" disabled={loadingPassword}>
                {loadingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <Key className="mr-2 h-4 w-4" />
                    Aggiorna Password
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <CardTitle className="text-red-900">Zona Pericolosa</CardTitle>
            </div>
            <CardDescription className="text-red-700">
              Azioni irreversibili sul tuo account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start gap-3">
                  <UserX className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Elimina Account</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Una volta eliminato il tuo account, non potrai più recuperarlo. Tutti i tuoi dati saranno cancellati definitivamente dal sistema, inclusi:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-4 list-disc">
                      <li>Profilo personale e dati anagrafici</li>
                      <li>Corsi creati o iscrizioni (a seconda del ruolo)</li>
                      <li>Cronologia pagamenti e transazioni</li>
                      <li>Progressi e certificati</li>
                      <li>Messaggi e notifiche</li>
                    </ul>
                    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Elimina il mio account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Sei assolutamente sicuro?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="space-y-4">
                            <p>
                              Questa azione è <strong>irreversibile</strong> e comporterà la cancellazione definitiva di tutti i tuoi dati dal sistema.
                            </p>
                            <div className="space-y-2">
                              <Label htmlFor="deleteConfirmation">
                                Digita <strong className="text-red-600">ELIMINA</strong> per confermare
                              </Label>
                              <Input
                                id="deleteConfirmation"
                                placeholder="ELIMINA"
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                disabled={loadingDelete}
                              />
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={loadingDelete}>
                            Annulla
                          </AlertDialogCancel>
                          <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={loadingDelete || deleteConfirmation !== 'ELIMINA'}
                          >
                            {loadingDelete ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Eliminazione...
                              </>
                            ) : (
                              <>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Elimina definitivamente
                              </>
                            )}
                          </Button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
